import {Input} from 'antd'
import {getId, getType} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import Pure from 'components/Pure'
import {useFormikContext} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {ask_fetchAskQuestions_Api, ask_updateAsk_Api} from '../../../apis/ask'
import {getLinkToDetail} from '../../../apis/model/ask'
import {StepsForm} from '../../../components/form/StepsForm'
import {FullSideModalLayout} from '../../../components/Modal/SideModalLayout'
import useAsyncAction from '../../../modules/asyncCache/useAsyncAction'
import useTranslate from '../../../modules/local/useTranslate'
import ServerContext from '../../Server/ServerContext'
import PostSettingForm from '../../../components/Feed/PostForm/PostSettingForm'
import {PostFormContext} from '../../../components/Feed/PostForm/PostFormContext'

// giữ nguyên — logic transform không đổi
const tranformBeforeSubmit = (values = {}) => ({...values})

// ─── Step 1 ───────────────────────────────────────────────────────────────────

const AskEditorForm1 = ({onSubmit, isLoading, initialValues}) => {
    const t = useTranslate()

    // Giữ nguyên toàn bộ destructure — các hook này cần thiết cho FieldsFactory
    const {
        resetForm,
        values,
        setSubmitting,
        setErrors,
        initialErrors,
        handleChange,
        ...rest
    } = useFormikContext()

    const {response, handleAsyncAction} = useAsyncAction({
        apiInfo: ask_fetchAskQuestions_Api,
    })

    // debounce giữ nguyên — search suggestion logic
    const debounced = _.debounce((e) => {
        handleAsyncAction({keyword: e.target.value})
    }, 300)

    const suggestions = _.take(_.get(response, 'data.data', []), 7)

    return (
        <div className="flex flex-col gap-4">

            {/* ── Channel selector — đồng bộ design system ── */}
            <PostSettingForm/>

            {/* ── Note ── */}
            <div className="p-3 border-l-3 border-slate-300 bg-slate-50 rounded-r-xl space-x-1 text-sm italic">
                <span className="font-semibold text-slate-500 flex-shrink-0">
                    {`* ${t('note')} : `}
                </span>
                <span className="text-slate-400">
                    {t('note create ask description')}
                </span>
            </div>

            {/* ── Câu hỏi — dùng FieldsFactory + Input giữ nguyên ── */}
            <div className="flex flex-col gap-3">
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            {
                                children: {
                                    type: 'text',
                                    name: 'title',
                                    label: t('what is your question'),
                                    component: Input,
                                    withProps: {
                                        type: 'text',
                                        minLength: 16,
                                        maxLength: 120,
                                        showCount: true,
                                        placeholder: t('enter your question or concern'),
                                        className: 'rounded-md',
                                        onChange: (e) => {
                                            handleChange(e)
                                            debounced(e)
                                        },
                                    },
                                },
                            },
                        ]}
                    />
                </Pure>

                {/* ── Suggestions — nhẹ, không lấn át input chính ── */}
                {!_.isEmpty(suggestions) && (
                    <div className="flex flex-col gap-1 border-t border-color-50 pt-2">
                        <span className="text-xs font-semibold text-color-400 uppercase tracking-wide">
                            {t('questions are already available')}
                        </span>
                        {suggestions.map((askItem, i) => (
                            <div
                                key={i}
                                className="text-sm rounded-md hover:background-100 transition-colors"
                                style={{padding: '3px 6px'}}>
                                <span className="text-color-400">{i + 1}. </span>
                                <Link
                                    target="_blank"
                                    to={getLinkToDetail(askItem)}
                                    className="font-medium text-color-100 no-underline hover:text-primary transition-colors">
                                    {getTitle(askItem)} ?
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Mô tả thêm — Input.TextArea giữ nguyên ── */}
            <Pure>
                <FieldsFactory
                    formSchema={[
                        {
                            children: {
                                type: 'text',
                                name: 'description',
                                label: t('describe more about your question'),
                                component: Input.TextArea,
                                withProps: {
                                    className: 'scrollBarThin',
                                    type: 'text',
                                    size: 'large',
                                    rows: 5,
                                    maxLength: 500,
                                    showCount: true,
                                    placeholder: t('description'),
                                },
                            },
                        },
                    ]}
                />
            </Pure>
        </div>
    )
}

const AskEditorRoutes = [
    {
        path: '/form-1',
        name: 'make a question',
        component: AskEditorForm1,
        exact: true,
    },
]

// ─── AskEditorForm ────────────────────────────────────────────────────────────
// Giữ nguyên hoàn toàn — StepsForm handle submit/validate/routing

const AskEditorForm = ({onCancel, onSubmit, isLoading, initialValues}) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}
        const title = String(values?.title ?? '')
        if (_.isEmpty(title.trim())) {
            errors.title = t('required field')
        }
        if (title.length < 16) {
            errors.title = t('name required more than {1} characters', {1: 16})
        }
        return errors
    }

    return (
        <StepsForm
            className="py-4"
            validate={validate}
            isLoading={isLoading}
            validateOnMount={true}
            validateOnChange={true}
            onSubmit={(values) => onSubmit(tranformBeforeSubmit(values))}
            routes={AskEditorRoutes}
            initialValues={initialValues}
            onCancel={onCancel}
        />
    )
}

// ─── EnhancedEditAsk ──────────────────────────────────────────────────────────
// Giữ nguyên hoàn toàn — logic phức tạp, chỉ tối ưu wrapper padding

export const EnhancedEditAsk = ({askItem, onCancel}) => {
    const t = useTranslate()
    const {options} = useContext(PostFormContext)
    const {item, setCurrent} = useContext(ServerContext)

    const {owner, container} = askItem ?? {}

    const {isLoading, handleAsyncAction: handleAsyncPost} = useAsyncAction({
        apiInfo: ask_updateAsk_Api,
        query: {':ask_id': askItem.id},
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            setCurrent(null)
            onCancel()
        },
        onError: notifyOnError(t),
    })

    // Giữ nguyên hoàn toàn — shape này PostSettingForm cần để hiển thị đúng
    const ownerOption = owner
        ? {
            value: getId(owner),
            data: owner,
            id: getId(owner),
            type: getType(owner),
            label: owner?.name ?? owner?.username ?? '',
        }
        : undefined

    const containerOption = container
        ? options.find((o) => o.value === getId(container)) ?? {
        value: getId(container),
        data: container,
        id: getId(container),
        type: getType(container),
        label: container?.title ?? container?.name ?? '',
    }
        : undefined

    const initialValues = {
        ..._.pick(askItem ?? {}, ['title', 'privacy', 'description']),
        containers: options,
        owner_disabled: true,
        container_disabled: true,
        default_owner_id: getId(owner),
        default_container_id: getId(container),
        owner: ownerOption,
        container: containerOption,
    }

    const handleClose = () => {
        setCurrent(null)
        onCancel()
    }

    return (
        <FullSideModalLayout
            right
            renderHeader={() => null}
            onCancel={handleClose}>
            {/* p-3 — wrapper bên ngoài đã có padding, không cần thêm */}
            <div className="p-3">
                <AskEditorForm
                    initialValues={initialValues}
                    title={t('edit')}
                    isLoading={isLoading}
                    onSubmit={(values) => {
                        const {
                            owner,
                            container,
                            containers,
                            owner_disabled,
                            container_disabled,
                            default_owner_id,
                            default_container_id,
                            ...rest
                        } = values ?? {}
                        // Giữ nguyên mapping logic
                        handleAsyncPost({
                            ...rest,
                            owner_id: getId(owner?.data ?? owner),
                            owner_type: getType(owner?.data ?? owner),
                            container_id: getId(container?.data ?? container),
                            container_type: getType(container?.data ?? container),
                        })
                    }}
                    onCancel={handleClose}
                />
            </div>
        </FullSideModalLayout>
    )
}

export default AskEditorForm